/**
 * HelpPanel Component
 * Displays contextual help content in a panel format
 * Pattern: Atomic Design - Organisms
 */

import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import type { HelpContent } from '../../types/helpContent.types';
import { Routes } from '../../../routes';

export interface HelpPanelProps {
  helpContent: HelpContent;
  'data-testid'?: string;
}

export function HelpPanel({
  helpContent,
  'data-testid': dataTestId = 'help-panel',
}: HelpPanelProps) {
  const navigate = useNavigate();

  // Map page titles to routes
  const getRouteForPage = (title: string): string | null => {
    const routeMap: Record<string, string> = {
      Gangs: Routes.gangs.manage,
      Users: Routes.users.manage,
      'Labour Groups': Routes.labour.groupsManage,
      'Labour Item Groups': Routes.labour.groupsManage,
      'Labour Items': Routes.labour.itemsManage,
      'Labour Item Definitions': Routes.labour.itemsManage,
      Sites: Routes.sites.manage,
      Finance: Routes.finance.pots,
      'Pot Approvals': Routes.finance.pots, // Backwards compatibility
    };
    return routeMap[title] || null;
  };

  const handlePageClick = (title: string) => {
    const route = getRouteForPage(title);
    if (route) {
      navigate(route);
    }
  };

  return (
    <Box sx={{ px: 3, pb: 3, pt: 1 }} data-testid={dataTestId}>
      {/* Main Description */}
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        {helpContent.description}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* What is it? Section */}
      {helpContent.sections.whatIsIt && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            What is this?
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, whiteSpace: 'pre-line', lineHeight: 1.6 }}
          >
            {helpContent.sections.whatIsIt}
          </Typography>
        </>
      )}

      {/* Additional Info Sections */}
      {helpContent.sections.additionalInfo &&
        Object.entries(helpContent.sections.additionalInfo).map(
          ([title, content]) => (
            <Box key={title} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
              >
                {content}
              </Typography>
            </Box>
          )
        )}

      {/* Common Tasks Section */}
      {helpContent.sections.commonTasks &&
        helpContent.sections.commonTasks.length > 0 && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              Common Tasks
            </Typography>
            <List dense sx={{ mb: 2 }}>
              {helpContent.sections.commonTasks.map((task, index) => (
                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                  <ListItemText
                    primary={task}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                    }}
                    sx={{
                      my: 0,
                      '& .MuiListItemText-primary:before': {
                        content: '"•"',
                        marginRight: 1,
                        fontWeight: 'bold',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

      {/* Example Scenarios Section */}
      {helpContent.sections.scenarios &&
        helpContent.sections.scenarios.length > 0 && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              Example Scenarios
            </Typography>
            <Box sx={{ mb: 2 }}>
              {helpContent.sections.scenarios.map((scenario, index) => (
                <Accordion
                  key={index}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:before': { display: 'none' },
                    mb: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ px: 2 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${index + 1}`}
                        size="small"
                        sx={{
                          minWidth: 28,
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {scenario.title}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, lineHeight: 1.6 }}
                    >
                      {scenario.description}
                    </Typography>

                    {scenario.steps && scenario.steps.length > 0 && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Steps:
                        </Typography>
                        <List dense sx={{ mb: 2 }}>
                          {scenario.steps.map((step, stepIndex) => (
                            <ListItem key={stepIndex} sx={{ py: 0.5, px: 0 }}>
                              <ListItemText
                                primary={step}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  color: 'text.secondary',
                                }}
                                sx={{
                                  my: 0,
                                  '& .MuiListItemText-primary:before': {
                                    content: `"${stepIndex + 1}."`,
                                    marginRight: 1,
                                    fontWeight: 'bold',
                                  },
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}

                    {scenario.result && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          Result:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          {scenario.result}
                        </Typography>
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </>
        )}

      {/* Related Pages Section */}
      {helpContent.sections.relatedPages &&
        helpContent.sections.relatedPages.length > 0 && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              Related Pages
            </Typography>
            <List dense sx={{ mb: 2 }}>
              {helpContent.sections.relatedPages.map((page, index) => {
                const route = getRouteForPage(page.title);
                return (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        cursor: route ? 'pointer' : 'default',
                        '&:hover': route
                          ? {
                              '& .MuiTypography-root:first-of-type': {
                                color: 'primary.main',
                                textDecoration: 'underline',
                              },
                            }
                          : {},
                      }}
                      onClick={() => handlePageClick(page.title)}
                    >
                      <ArrowForwardIcon
                        sx={{
                          fontSize: 16,
                          color: 'primary.main',
                          mt: 0.25,
                          mr: 1,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 0.25,
                            transition: 'color 0.2s',
                          }}
                        >
                          {page.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          {page.description}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}

      {/* Documentation Link */}
      {helpContent.documentationUrl && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Link
            href={helpContent.documentationUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArticleIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Read Full Documentation
            </Typography>
          </Link>
        </>
      )}
    </Box>
  );
}
